import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, RefreshCw, Download } from 'lucide-react';

const API_V2 = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;
const PAIRS = ['BTC-USD', 'ETH-USD', 'SOL-USD', 'XRP-USD', 'DOGE-USD', 'SHIB-USD', 'ADA-USD', 'AVAX-USD'];

const SellAuditPage = () => {
  const navigate = useNavigate();
  const [pair, setPair] = useState('BTC-USD');
  const [limit, setLimit] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [archives, setArchives] = useState([]);
  const [undoing, setUndoing] = useState(false);
  const [accessLevel, setAccessLevel] = useState(null);

  // GOD-level gate
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_V2}/user/bot/status`, { withCredentials: true });
        setAccessLevel(res.data?.access_level || 'FREE');
      } catch {
        setAccessLevel('FREE');
      }
    })();
  }, []);

  const fetchData = async (selectedPair = pair, selectedLimit = limit) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_V2}/user/bot/diag/sell-audit?pair=${selectedPair}&limit=${selectedLimit}`,
        { withCredentials: true }
      );
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadJson = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sell-audit-${pair}-${new Date().toISOString().slice(0, 19)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const doReset = async (mode = 'avg_buy') => {
    let msg, body;
    if (mode === 'market') {
      msg = `RESET BUY LOTS for ${pair} at CURRENT MARKET PRICE?\n\nThis will archive all historical trades and create one synthetic lot at the live price.\n\nUse this if you want a clean break-even reset (not preserving true cost basis).`;
      body = { pair, confirm: true, use_avg_buy: false };
    } else {
      msg = `RESET BUY LOTS for ${pair} at your TRUE AVG BUY PRICE?\n\nThis preserves the cost basis of your remaining lots — bot targets stay aligned with what you actually paid.\n\nRecommended.`;
      body = { pair, confirm: true, use_avg_buy: true };
    }
    if (!window.confirm(msg)) return;
    setResetting(true);
    setResetResult(null);
    try {
      const res = await axios.post(`${API_V2}/user/bot/diag/reset-buy-lots`, body, { withCredentials: true });
      setResetResult(res.data);
      await fetchData();
      await fetchArchives();
    } catch (e) {
      setResetResult({ error: e.response?.data?.detail || e.response?.data?.error || e.message });
    } finally {
      setResetting(false);
    }
  };
  
  const fetchArchives = async () => {
    try {
      const res = await axios.get(`${API_V2}/user/bot/diag/list-archives`, { withCredentials: true });
      setArchives(res.data?.archives || []);
    } catch {
      setArchives([]);
    }
  };
  
  const doUndo = async (archive_tag) => {
    if (!window.confirm(`Undo this reset?\n\nArchive: ${archive_tag}\n\nThis restores the original trades and deletes the synthetic lot. Restart the bot after to rebuild buy_lots.`)) return;
    setUndoing(true);
    setResetResult(null);
    try {
      const res = await axios.post(`${API_V2}/user/bot/diag/restore-buy-lots`,
        { archive_tag, confirm: true },
        { withCredentials: true }
      );
      setResetResult(res.data);
      await fetchData();
      await fetchArchives();
    } catch (e) {
      setResetResult({ error: e.response?.data?.detail || e.response?.data?.error || e.message });
    } finally {
      setUndoing(false);
    }
  };

  useEffect(() => { fetchArchives(); }, []);

  // Show gate while loading access level
  if (accessLevel === null) {
    return (
      <div className="min-h-screen bg-black text-[#00FF41] flex items-center justify-center font-mono">
        <span className="text-sm">Loading...</span>
      </div>
    );
  }
  // GOD-only gate
  if (accessLevel !== 'GOD') {
    return (
      <div className="min-h-screen bg-black text-[#00FF41] flex flex-col items-center justify-center font-mono p-6 text-center">
        <span className="text-red-400 text-lg mb-2">⛔ GOD-LEVEL ACCESS REQUIRED</span>
        <span className="text-[#008F11] text-xs mb-4">The diagnostic page is restricted to admin users.</span>
        <button
          onClick={() => navigate('/dashboard')}
          className="text-xs bg-[#003B00] hover:bg-[#005500] text-[#00FF41] px-4 py-2 border border-[#00FF41]"
          data-testid="diag-gate-back-btn"
        >
          ← BACK TO DASHBOARD
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-[#00FF41] p-3 md:p-6 font-mono">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-1 text-xs text-[#008F11] hover:text-[#00FF41]"
              data-testid="diag-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> BACK
            </button>
            <h1 className="text-lg md:text-xl tracking-widest">SELL AUDIT — DIAGNOSTIC</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={pair}
              onChange={(e) => { setPair(e.target.value); fetchData(e.target.value, limit); }}
              className="bg-black border border-[#003B00] text-[#00FF41] px-2 py-1 text-xs"
              data-testid="diag-pair-select"
            >
              {PAIRS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select
              value={limit}
              onChange={(e) => { setLimit(Number(e.target.value)); fetchData(pair, Number(e.target.value)); }}
              className="bg-black border border-[#003B00] text-[#00FF41] px-2 py-1 text-xs"
              data-testid="diag-limit-select"
            >
              {[10, 30, 50, 100].map(n => <option key={n} value={n}>{n} rows</option>)}
            </select>
            <button
              onClick={() => fetchData()}
              disabled={loading}
              className="flex items-center gap-1 text-xs bg-[#003B00] hover:bg-[#005500] text-[#00FF41] px-3 py-1 border border-[#00FF41]"
              data-testid="diag-refresh-btn"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              REFRESH
            </button>
            <button
              onClick={downloadJson}
              disabled={!data}
              className="flex items-center gap-1 text-xs bg-[#003B00] hover:bg-[#005500] text-[#00FF41] px-3 py-1 border border-[#00FF41] disabled:opacity-50"
              data-testid="diag-download-btn"
            >
              <Download className="w-3 h-3" /> JSON
            </button>
            <button
              onClick={() => doReset('avg_buy')}
              disabled={resetting || !data}
              className="flex items-center gap-1 text-xs bg-green-950 hover:bg-green-900 text-green-300 px-3 py-1 border border-green-700 disabled:opacity-50"
              data-testid="diag-reset-avg-buy-btn"
              title={`Reset ${pair} at your true blended avg buy price (preserves cost basis)`}
            >
              {resetting ? <RefreshCw className="w-3 h-3 animate-spin" /> : '↻'} RESET @ AVG BUY
            </button>
            <button
              onClick={() => doReset('market')}
              disabled={resetting || !data}
              className="flex items-center gap-1 text-xs bg-red-950 hover:bg-red-900 text-red-400 px-3 py-1 border border-red-700 disabled:opacity-50"
              data-testid="diag-reset-market-btn"
              title={`Reset ${pair} at current market price (loses cost basis)`}
            >
              {resetting ? <RefreshCw className="w-3 h-3 animate-spin" /> : '⚠'} RESET @ MARKET
            </button>
          </div>
        </div>

        {archives.length > 0 && (
          <div className="border border-yellow-700/50 bg-yellow-950/10 p-3 mb-4" data-testid="archives-panel">
            <div className="flex items-center justify-between mb-2">
              <span className="text-yellow-400 text-xs font-bold tracking-widest">EXISTING ARCHIVES ({archives.length}) — UNDO ANY RESET</span>
            </div>
            <div className="space-y-1">
              {archives.map((a) => (
                <div key={a.archive_tag} className="flex items-center justify-between gap-2 text-[11px] border border-[#003B00] bg-[#0a0a0a] p-1.5">
                  <div className="flex-1 min-w-0">
                    <div className="text-[#00FF41] truncate font-mono">{a.archive_tag}</div>
                    <div className="text-[#666] text-[10px]">{a.pair || '?'} ({a.mode || '?'}) — {a.trade_count} trades archived</div>
                  </div>
                  <button
                    onClick={() => doUndo(a.archive_tag)}
                    disabled={undoing}
                    className="text-[10px] bg-yellow-900 hover:bg-yellow-800 text-yellow-200 px-2 py-1 border border-yellow-600 disabled:opacity-50 shrink-0"
                    data-testid={`undo-${a.archive_tag}`}
                  >
                    {undoing ? '...' : 'UNDO'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="border border-red-700 bg-red-950/30 p-3 mb-4 text-red-400 text-xs">
            ERROR: {error}
          </div>
        )}

        {resetResult && (
          <div className={`border p-3 mb-4 text-xs ${resetResult.error ? 'border-red-700 bg-red-950/30 text-red-400' : 'border-green-700 bg-green-950/30 text-green-400'}`} data-testid="reset-result">
            <div className="font-bold mb-1">{resetResult.error ? 'RESET FAILED' : 'RESET COMPLETE'}</div>
            <pre className="whitespace-pre-wrap text-[10px]">{JSON.stringify(resetResult, null, 2)}</pre>
            {!resetResult.error && resetResult.archive_tag && (
              <div className="mt-2 text-yellow-400 text-[10px]">
                Want to undo? Use POST /api/v2/user/bot/diag/restore-buy-lots with archive_tag="{resetResult.archive_tag}"
              </div>
            )}
          </div>
        )}

        {loading && !data && (
          <div className="text-[#008F11] text-sm">Loading...</div>
        )}

        {data && (
          <div className="space-y-4 text-xs">
            {/* Settings */}
            <Section title="BOT SETTINGS">
              <KV data={data.settings} />
            </Section>

            {/* HISTORY SUMMARY — buy price distribution shows if there are older lots at different price ranges */}
            {data.summary && (
              <Section title={`TRADE HISTORY SUMMARY (${data.pair} — ${data.mode})`}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                  <Stat label="Total BUYs" value={data.summary.total_buys_in_history} />
                  <Stat label="Total SELLs" value={data.summary.total_sells_in_history} />
                  <Stat label="Total BUY $" value={`$${data.summary.total_buy_usd.toFixed(2)}`} />
                  <Stat label="Total SELL $" value={`$${data.summary.total_sell_usd.toFixed(2)}`} />
                </div>
                <div className="text-[#666] text-[10px] uppercase tracking-wider mb-1">BUY PRICE DISTRIBUTION (lots at each $1k bucket)</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {Object.entries(data.summary.buy_price_distribution || {})
                    .sort((a, b) => parseInt(a[0].slice(1)) - parseInt(b[0].slice(1)))
                    .map(([range, count]) => (
                      <div key={range} className="border border-[#003B00] p-1.5 text-[11px]">
                        <span className="text-[#008F11]">{range}: </span>
                        <span className="text-[#00FF41] font-bold">{count} {count === 1 ? 'buy' : 'buys'}</span>
                      </div>
                    ))}
                </div>
              </Section>
            )}

            {/* Lots */}
            <Section title={`LIVE SESSION LOTS (in-memory) — ${data.live_session_lot_count}`}>
              {data.live_session_lots.length === 0 ? (
                <div className="text-[#666]">No live lots</div>
              ) : (
                <Table cols={['#', 'amount', 'buy_price', 'cost']} rows={data.live_session_lots.map((l, i) => ({
                  '#': i + 1, amount: l.amount.toFixed(8), buy_price: `$${l.buy_price.toFixed(4)}`, cost: `$${l.cost.toFixed(2)}`
                }))} />
              )}
            </Section>

            <Section title={`FIFO RECONSTRUCTED LOTS (from DB trades) — ${data.fifo_reconstructed_lot_count}`}>
              {data.fifo_reconstructed_lots.length === 0 ? (
                <div className="text-[#666]">No remaining lots after FIFO replay</div>
              ) : (
                <Table cols={['#', 'amount', 'buy_price', 'cost']} rows={data.fifo_reconstructed_lots.map((l, i) => ({
                  '#': i + 1, amount: l.amount.toFixed(8), buy_price: `$${l.buy_price.toFixed(4)}`, cost: `$${l.cost.toFixed(2)}`
                }))} />
              )}
            </Section>

            {/* Recent SELLS */}
            <Section title={`RECENT SELLS — ${data.recent_sells.length} (newest first)`}>
              {data.recent_sells.length === 0 ? (
                <div className="text-[#666]">No sells yet</div>
              ) : (
                <Table
                  cols={['timestamp', 'amount', 'price', 'usd_value', 'profit', 'trigger', 'lots_sold', 'order_id', 'synced']}
                  rows={data.recent_sells.map(s => ({
                    timestamp: (s.timestamp || '').slice(0, 19),
                    amount: Number(s.amount || 0).toFixed(8),
                    price: `$${Number(s.price || 0).toFixed(2)}`,
                    usd_value: `$${Number(s.usd_value || 0).toFixed(2)}`,
                    profit: `${Number(s.profit || 0) >= 0 ? '+' : ''}$${Number(s.profit || 0).toFixed(4)}`,
                    trigger: s.trigger || '?',
                    lots_sold: s.lots_sold ?? '?',
                    order_id: (s.order_id || '').slice(0, 16),
                    synced: s.synced_from_exchange ? 'YES' : '',
                  }))}
                  colorRow={(r) => r.profit?.startsWith('+') ? 'text-[#00FF41]' : 'text-[#FF4444]'}
                />
              )}
            </Section>

            {/* Sell attribution */}
            <Section title="FIFO SELL ATTRIBUTION (which lots each sell ate)">
              {data.fifo_sell_attribution.length === 0 ? (
                <div className="text-[#666]">No attribution data</div>
              ) : (
                <div className="space-y-2">
                  {data.fifo_sell_attribution.slice().reverse().map((s, i) => (
                    <div key={i} className="border border-[#003B00] p-2 bg-[#050505]">
                      <div className="flex items-center justify-between flex-wrap gap-1 mb-1 text-[11px]">
                        <span className="text-[#00FF41]">
                          {(s.timestamp || '').slice(0, 19)} — sold {Number(s.amount_sold).toFixed(8)} (${Number(s.usd_value).toFixed(2)})
                        </span>
                        <span className="text-[#008F11]">
                          {s.trigger} {s.synced && '[SYNCED]'} {s.order_id && `oid=${s.order_id.slice(0, 12)}`}
                        </span>
                      </div>
                      <div className="pl-3 space-y-0.5">
                        {s.consumed_lots.map((cl, j) => (
                          <div key={j} className="text-[#666] text-[10px]">
                            ↳ {cl.full_lot ? 'FULL' : 'PARTIAL'} lot @ ${Number(cl.from_lot_buy_price).toFixed(4)} → consumed {Number(cl.consumed).toFixed(8)}
                            {!cl.full_lot && <span className="text-yellow-500"> (leaves {Number(cl.partial_remaining).toFixed(8)} in lot)</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Recent BUYS */}
            <Section title={`RECENT BUYS — ${data.recent_buys.length} (newest first)`}>
              {data.recent_buys.length === 0 ? (
                <div className="text-[#666]">No buys</div>
              ) : (
                <Table
                  cols={['timestamp', 'amount', 'price', 'usd_value', 'trigger', 'order_id', 'synced']}
                  rows={data.recent_buys.map(b => ({
                    timestamp: (b.timestamp || '').slice(0, 19),
                    amount: Number(b.amount || 0).toFixed(8),
                    price: `$${Number(b.price || 0).toFixed(2)}`,
                    usd_value: `$${Number(b.usd_value || 0).toFixed(2)}`,
                    trigger: b.trigger || '?',
                    order_id: (b.order_id || '').slice(0, 16),
                    synced: b.synced_from_exchange ? 'YES' : '',
                  }))}
                />
              )}
            </Section>

            {/* Raw JSON */}
            <Section title="RAW JSON (click to copy/download)">
              <pre className="bg-[#050505] border border-[#003B00] p-2 overflow-auto text-[10px] max-h-[400px] whitespace-pre-wrap">
                {JSON.stringify(data, null, 2)}
              </pre>
            </Section>
          </div>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="border border-[#003B00] bg-[#0a0a0a]">
    <div className="bg-[#001500] px-3 py-1 text-[#00FF41] text-[10px] tracking-widest font-bold">
      {title}
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const KV = ({ data }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
    {Object.entries(data || {}).map(([k, v]) => (
      <div key={k} className="border border-[#003B00]/40 p-1.5">
        <div className="text-[#666] text-[9px] uppercase tracking-wider">{k}</div>
        <div className="text-[#00FF41]">{String(v ?? 'null')}</div>
      </div>
    ))}
  </div>
);

const Stat = ({ label, value }) => (
  <div className="border border-[#003B00] p-2 bg-[#000]">
    <div className="text-[#666] text-[9px] uppercase tracking-wider">{label}</div>
    <div className="text-[#00FF41] font-mono font-bold text-sm">{value}</div>
  </div>
);

const Table = ({ cols, rows, colorRow }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-[11px]">
      <thead>
        <tr className="text-[#666] uppercase tracking-wider border-b border-[#003B00]">
          {cols.map(c => <th key={c} className="text-left py-1 px-2">{c}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className={`border-b border-[#003B00]/30 hover:bg-[#0f0f0f] ${colorRow ? colorRow(r) : 'text-[#00FF41]'}`}>
            {cols.map(c => <td key={c} className="py-1 px-2 font-mono">{r[c]}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default SellAuditPage;
